import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

import ProctoringAdminService from "../../../_services/ProctoringAdminService";
import WebProctoringPanel from "../../quick-quiz-maker/WebProctoringPanel";

interface SessionDetailModalProps {
  show: boolean;
  onHide: () => void;
  testId: number | null;
  enquiryId: number | null;
  onReviewSubmitted?: () => void;
  onStudentBlocked?: () => void;
}

interface Violation {
  Id: number;
  ViolationType: string;
  Metadata: string;
  EntryDate: string;
}

interface Recording {
  id: number;
  recordingType: string;
  s3Key: string;
  chunkIndex: number;
  fileSizeBytes: number;
  status: string;
  createdAt: string;
  playbackUrl: string;
}

interface Review {
  Id: number;
  ReviewStatus: string;
  Notes: string;
  ReviewedAt: string;
  reviewerName: string;
}

interface SessionData {
  student: { studentName: string; testName: string };
  violations: Violation[];
  recordings: Recording[];
  reviews: Review[];
}

const VIOLATION_ICONS: Record<string, string> = {
  TabSwitch: "Tab Switch",
  FullscreenExit: "Fullscreen Exit",
  FocusLoss: "Focus Loss",
  RightClick: "Right Click",
  DevToolsAttempt: "Dev Tools",
  ScreenShareRevoked: "Screen Share Revoked",
};

const STATUS_COLORS: Record<string, string> = {
  flagged: "#dc2626",
  cleared: "#16a34a",
  needs_review: "#d97706",
  blocked: "#7c3aed",
};

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  show,
  onHide,
  testId,
  enquiryId,
  onReviewSubmitted,
  onStudentBlocked,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SessionData | null>(null);
  const [activeTab, setActiveTab] = useState<"screen" | "webcam">("screen");
  const [activeChunkIndex, setActiveChunkIndex] = useState(0);
  const [reviewStatus, setReviewStatus] = useState("flagged");
  const [reviewNotes, setReviewNotes] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blocking, setBlocking] = useState(false);
  // Webcam-AI snapshot evidence (DynamoDB pipeline) — distinct from the
  // "Webcam" recording tab below, which plays the browser pipeline's backup
  // VIDEO chunks. This swaps the modal body for WebProctoringPanel: profile
  // match, per-capture Pass/Fail metrics, photos, and the Test/Blanket Block
  // levers.
  const [showWebcamEvidence, setShowWebcamEvidence] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (show && testId && enquiryId) {
      loadDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, testId, enquiryId]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await ProctoringAdminService.getSessionDetail(testId!, enquiryId!);
      setData(res.data.data);
      setActiveChunkIndex(0);
    } catch (err) {
      console.error("Failed to load session detail:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!testId || !enquiryId) return;
    setSubmittingReview(true);
    try {
      await ProctoringAdminService.reviewSession({
        testId,
        enquiryId,
        reviewStatus,
        notes: reviewNotes,
      });
      setReviewNotes("");
      await loadDetail();
      onReviewSubmitted?.();
    } catch (err) {
      console.error("Failed to submit review:", err);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleBlock = async () => {
    if (!testId || !enquiryId) return;
    setBlocking(true);
    try {
      await ProctoringAdminService.blockStudent({
        testId,
        enquiryId,
        reason: blockReason,
      });
      setShowBlockConfirm(false);
      setBlockReason("");
      await loadDetail();
      onStudentBlocked?.();
    } catch (err) {
      console.error("Failed to block student:", err);
    } finally {
      setBlocking(false);
    }
  };

  const handleExited = () => {
    setData(null);
    setActiveTab("screen");
    setActiveChunkIndex(0);
    setReviewStatus("flagged");
    setReviewNotes("");
    setShowBlockConfirm(false);
    setBlockReason("");
    setShowWebcamEvidence(false);
  };

  const filteredRecordings =
    data?.recordings.filter((r) => r.recordingType === activeTab) || [];
  const currentRecording = filteredRecordings[activeChunkIndex] || null;

  const formatTime = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-IN", { hour12: false });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        onExited={handleExited}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {showWebcamEvidence ? "Webcam AI Evidence" : "Session Detail"}
            {data && `: ${data.student.studentName} — ${data.student.testName}`}
          </Modal.Title>
          <button
            type="button"
            onClick={onHide}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "70vh",
            // Evidence panel is a card-based page design — sit its cards on
            // the page canvas so it doesn't float as a slab in white padding.
            background: showWebcamEvidence ? "var(--surface-page)" : undefined,
          }}
        >
          {showWebcamEvidence && testId && enquiryId ? (
            <WebProctoringPanel
              testId={testId}
              enquiryId={enquiryId}
              onBack={() => setShowWebcamEvidence(false)}
            />
          ) : loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          ) : !data ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
              No data available.
            </div>
          ) : (
            <>
              {/* Recording Playback */}
              <div
                style={{
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 12,
                    alignItems: "center",
                  }}
                >
                  <strong style={{ fontSize: 14 }}>Recording Playback</strong>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <Button
                      size="sm"
                      variant={activeTab === "screen" ? "primary" : "secondary"}
                      onClick={() => {
                        setActiveTab("screen");
                        setActiveChunkIndex(0);
                      }}
                    >
                      Screen
                    </Button>
                    <Button
                      size="sm"
                      variant={activeTab === "webcam" ? "primary" : "secondary"}
                      onClick={() => {
                        setActiveTab("webcam");
                        setActiveChunkIndex(0);
                      }}
                    >
                      Webcam
                    </Button>
                  </div>
                </div>

                {filteredRecordings.length === 0 ? (
                  <div style={{ color: "var(--text-muted)", fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                    No {activeTab} recordings available.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        background: "#000",
                        borderRadius: 6,
                        marginBottom: 10,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <video
                        ref={videoRef}
                        key={currentRecording?.playbackUrl}
                        src={currentRecording?.playbackUrl || ""}
                        controls
                        style={{
                          maxWidth: "100%",
                          maxHeight: 360,
                          borderRadius: 6,
                        }}
                      />
                    </div>

                    {/* Chunk selector */}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {filteredRecordings.map((rec, idx) => (
                        <button
                          key={rec.id}
                          onClick={() => setActiveChunkIndex(idx)}
                          style={{
                            padding: "3px 10px",
                            fontSize: 12,
                            border: "1px solid var(--border-strong)",
                            borderRadius: 4,
                            background:
                              idx === activeChunkIndex ? "#2563eb" : "#fff",
                            color: idx === activeChunkIndex ? "#fff" : "#333",
                            cursor: "pointer",
                          }}
                        >
                          Chunk {idx + 1}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Violation Timeline */}
              <div
                style={{
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <strong style={{ fontSize: 14, display: "block", marginBottom: 10 }}>
                  Violation Timeline ({data.violations.length})
                </strong>
                {data.violations.length === 0 ? (
                  <div style={{ color: "var(--status-success)", fontSize: 13 }}>
                    No violations recorded.
                  </div>
                ) : (
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    <table style={{ width: "100%", fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--border-default)" }}>
                          <th style={{ padding: "4px 8px", fontWeight: 600 }}>Time</th>
                          <th style={{ padding: "4px 8px", fontWeight: 600 }}>Type</th>
                          <th style={{ padding: "4px 8px", fontWeight: 600 }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.violations.map((v, idx) => {
                          let meta: Record<string, any> = {};
                          try {
                            meta = typeof v.Metadata === "string" ? JSON.parse(v.Metadata) : v.Metadata || {};
                          } catch {
                            /* ignore */
                          }
                          return (
                            <tr
                              key={v.Id}
                              style={{
                                borderBottom: "1px solid var(--border-subtle)",
                                background: idx % 2 === 0 ? "#fff" : "#f9f9f9",
                              }}
                            >
                              <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                                {formatTime(v.EntryDate)}
                              </td>
                              <td style={{ padding: "4px 8px" }}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "1px 8px",
                                    borderRadius: 4,
                                    background: "var(--status-danger-bg)",
                                    color: "var(--status-danger)",
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  {VIOLATION_ICONS[v.ViolationType] || v.ViolationType}
                                </span>
                              </td>
                              <td style={{ padding: "4px 8px", color: "var(--text-muted)" }}>
                                {meta.count ? `Count: ${meta.count}` : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Review Actions */}
              <div
                style={{
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <strong style={{ fontSize: 14, display: "block", marginBottom: 10 }}>
                  Review Actions
                </strong>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>Status</Form.Label>
                    <Form.Select
                      size="sm"
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                    >
                      <option value="flagged">Flagged</option>
                      <option value="cleared">Cleared</option>
                      <option value="needs_review">Needs Review</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group style={{ flex: 1, minWidth: 200 }}>
                    <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>Notes</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Review notes..."
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleSubmitReview}
                    disabled={submittingReview}
                  >
                    {submittingReview ? "Submitting..." : "Submit Review"}
                  </Button>
                </div>

                {/* Review History */}
                {data.reviews.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--text-muted)" }}>
                      Review History
                    </div>
                    {data.reviews.map((r) => (
                      <div
                        key={r.Id}
                        style={{
                          fontSize: 12,
                          padding: "4px 0",
                          borderBottom: "1px solid var(--border-subtle)",
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                          {formatDateTime(r.ReviewedAt)}
                        </span>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "1px 8px",
                            borderRadius: 4,
                            background: STATUS_COLORS[r.ReviewStatus]
                              ? `${STATUS_COLORS[r.ReviewStatus]}15`
                              : "#f0f0f0",
                            color: STATUS_COLORS[r.ReviewStatus] || "#333",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {r.ReviewStatus}
                        </span>
                        <span style={{ color: "var(--text-body)" }}>
                          by {r.reviewerName}
                        </span>
                        {r.Notes && (
                          <span style={{ color: "var(--text-faint)" }}>— {r.Notes}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {showWebcamEvidence ? (
            <>
              <Button
                size="sm"
                variant="primary"
                style={{ marginRight: "auto" }}
                onClick={() => setShowWebcamEvidence(false)}
              >
                ← Back to Session Detail
              </Button>
              <Button size="sm" variant="secondary" onClick={onHide}>
                Close
              </Button>
            </>
          ) : !showBlockConfirm ? (
            <>
              <Button
                size="sm"
                variant="primary"
                style={{ marginRight: "auto" }}
                onClick={() => setShowWebcamEvidence(true)}
              >
                Webcam AI Evidence
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setShowBlockConfirm(true)}
              >
                Block Student
              </Button>
              <Button size="sm" variant="secondary" onClick={onHide}>
                Close
              </Button>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Reason for blocking..."
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                size="sm"
                variant="danger"
                onClick={handleBlock}
                disabled={blocking}
              >
                {blocking ? "Blocking..." : "Confirm Block"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowBlockConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SessionDetailModal;
